import { useDocTtsEntry } from '../state/docTtsStore';

interface Props {
  onClose?: () => void;
  /** Phase 5: render the compact composer-slot variant (relative, no sticky/shadow)
   *  so the bar sits inside the BottomComposer pill band instead of floating. */
  composer?: boolean;
  /** Phase 5: when provided, render a ⌄ "hide" button that collapses the bar WITHOUT
   *  stopping narration (the composer slot returns to the input row; a re-show chip
   *  lives in the chat control cluster). Only the chat-slot mount passes this — the
   *  doc viewer (CodeViewer) omits it, so docs never get a hide button. */
  onHide?: () => void;
}

export function DocTtsPlaybackBar({ onClose, composer = false, onHide }: Props) {
  const {
    phase, segments, currentSegment, playbackRate, autoCenter,
    pause, resume, stop, seekTo, cyclePlaybackRate, setAutoCenter,
  } = useDocTtsEntry();

  if (phase === 'idle' || phase === 'stopped') return null;

  const total = segments.length;
  const pct = total > 0 ? Math.round((currentSegment / total) * 100) : 0;

  const handleProgressClick = (e: MouseEvent) => {
    const bar = e.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(Math.floor(fraction * total));
  };

  const prevSeg = () => seekTo(Math.max(0, currentSegment - 1));
  const nextSeg = () => seekTo(Math.min(total - 1, currentSegment + 1));
  const restart = () => seekTo(0);

  return (
    <div class={`doc-tts-bar${composer ? ' doc-tts-bar--composer' : ''}`}>
      <div class="doc-tts-bar-info">
        {phase === 'loading' && <span class="doc-tts-spinner" />}
        {phase !== 'loading' && <span class="doc-tts-icon">🔊</span>}
        <span class="doc-tts-position">
          Para {currentSegment + 1} / {total}
        </span>
      </div>

      {/* Clickable progress bar */}
      <div class="doc-tts-progress" onClick={handleProgressClick} title="Click to seek">
        <div class="doc-tts-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      {/* Controls */}
      <div class="doc-tts-controls">
        <button class="doc-tts-btn" onClick={restart} title="Restart">↺</button>
        <button class="doc-tts-btn" onClick={prevSeg} title="Previous paragraph">⏮</button>
        {phase === 'playing' ? (
          <button class="doc-tts-btn doc-tts-btn--primary" onClick={pause} title="Pause">⏸</button>
        ) : (
          <button class="doc-tts-btn doc-tts-btn--primary" onClick={resume} title="Play">▶</button>
        )}
        <button class="doc-tts-btn" onClick={nextSeg} title="Next paragraph">⏭</button>
        <button class="doc-tts-btn" onClick={() => { stop(); onClose?.(); }} title="Stop">⏹</button>
        {/* Manual "center on current paragraph" — only shown when auto-center is OFF (#1564) */}
        {!autoCenter && (
          <button
            class="doc-tts-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('doc-tts-center'))}
            title="Center on current paragraph"
          >⊙</button>
        )}
        {/* Toggle: follow the playhead automatically (default OFF) */}
        <button
          class={`doc-tts-btn${autoCenter ? ' doc-tts-btn--primary' : ''}`}
          onClick={() => setAutoCenter(!autoCenter)}
          title={autoCenter ? 'Auto-scroll to current paragraph: ON (tap to turn off)' : 'Auto-scroll to current paragraph: OFF (tap to turn on)'}
          aria-pressed={autoCenter}
        >🎯</button>
        {/* Single cycling speed pill (1× → 1.5× → 2×), mirroring the chat playback pill.
            Replaces the former three buttons — frees the row so the conditional ⊙
            manual-center button (#1564) no longer pushes 1.5×/2× out of the panel. */}
        <span class="doc-tts-speed">
          <button
            class="doc-tts-speed-btn active"
            onClick={cyclePlaybackRate}
            title={`Read-aloud speed: ${playbackRate}× — tap to change`}
            aria-label={`Read-aloud speed ${playbackRate}×, tap to change`}
          >
            {playbackRate}×
          </button>
        </span>
        {/* Phase 5: hide the slot bar WITHOUT stopping narration (composer-slot
            mount only). A re-show chip in the chat control cluster brings it back.
            Distinct from ⏹ Stop — narration keeps playing while hidden. */}
        {onHide && (
          <button
            class="doc-tts-btn doc-tts-btn--hide"
            onClick={onHide}
            title="Hide controls (keeps playing)"
            aria-label="Hide read-aloud controls; narration continues"
          >⌄</button>
        )}
      </div>
    </div>
  );
}
